import React from "react";
import PropTypes from "prop-types";
import Skeleton from "./Skeleton";

export default function TableSkeleton({
  columns = 6,
  rows = 6,
  hasHeader = true,
  hasCheckbox = false,
  hasActions = true,
  variant = "table",
  className = "",
  style = {},
  columnWidths,
  ...rest
}) {
  const colCount = typeof columns === "number" ? columns : columns.length;

  const defaultWidths = [
    "120px",
    "200px",
    "130px",
    "110px",
    "150px",
    "110px",
    "90px",
    "140px",
  ];

  const getColWidth = (index) => {
    if (Array.isArray(columnWidths) && columnWidths[index]) {
      return columnWidths[index];
    }
    if (Array.isArray(columns) && columns[index]) {
      return columns[index];
    }
    return defaultWidths[index % defaultWidths.length];
  };

  if (variant === "div" || variant === "dt") {
    return (
      <div
        className={`app-skeleton-table-wrap ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        {...rest}
      >
        <span className="skeleton-sr-only">Loading table records…</span>
        {hasHeader && (
          <div
            className="app-skeleton-row"
            data-testid="table-skeleton-header"
            style={{ background: "rgba(0,0,0,0.02)" }}
          >
            {hasCheckbox && (
              <Skeleton variant="rounded" width={18} height={18} radius={4} />
            )}
            {Array.from({ length: colCount }, (_, idx) => (
              <span key={idx} style={{ flex: 1, minWidth: 60 }}>
                <Skeleton width={getColWidth(idx)} height={12} radius={4} />
              </span>
            ))}
            {hasActions && <Skeleton width={28} height={18} radius={4} />}
          </div>
        )}
        <div className="app-skeleton-row-stack">
          {Array.from({ length: rows }, (_, rowIdx) => (
            <div className="app-skeleton-row" key={rowIdx}>
              {hasCheckbox && (
                <Skeleton variant="rounded" width={18} height={18} radius={4} />
              )}
              {Array.from({ length: colCount }, (_, colIdx) => {
                const isBadge = colIdx === 4 || colIdx === 5;
                return (
                  <span key={colIdx} style={{ flex: 1, minWidth: 60 }}>
                    <Skeleton
                      variant={isBadge ? "pill" : "bar"}
                      width={isBadge ? "80px" : getColWidth(colIdx)}
                      height={isBadge ? 22 : 13}
                      radius={isBadge ? 999 : 4}
                    />
                  </span>
                );
              })}
              {hasActions && (
                <Skeleton variant="circular" width={26} height={26} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "rows") {
    return (
      <>
        {Array.from({ length: rows }, (_, rowIdx) => (
          <tr key={rowIdx} className="app-skeleton-tr">
            {hasCheckbox && (
              <td style={{ width: 40, textAlign: "center" }}>
                <Skeleton variant="rounded" width={18} height={18} radius={4} />
              </td>
            )}
            {Array.from({ length: colCount }, (_, colIdx) => (
              <td key={colIdx}>
                <Skeleton width={getColWidth(colIdx)} height={13} radius={4} />
              </td>
            ))}
            {hasActions && (
              <td style={{ width: 60, textAlign: "center" }}>
                <Skeleton variant="circular" width={24} height={24} />
              </td>
            )}
          </tr>
        ))}
      </>
    );
  }

  // Default "table" variant
  return (
    <div
      className={`app-skeleton-table-wrap ${className}`.trim()}
      style={style}
      role="status"
      aria-busy="true"
      {...rest}
    >
      <span className="skeleton-sr-only">Loading table records…</span>
      <table className="app-skeleton-table">
        {hasHeader && (
          <thead>
            <tr>
              {hasCheckbox && (
                <th style={{ width: 40, textAlign: "center" }}>
                  <Skeleton
                    variant="rounded"
                    width={18}
                    height={18}
                    radius={4}
                  />
                </th>
              )}
              {Array.from({ length: colCount }, (_, colIdx) => (
                <th key={colIdx}>
                  <Skeleton
                    width={getColWidth(colIdx)}
                    height={12}
                    radius={4}
                  />
                </th>
              ))}
              {hasActions && (
                <th style={{ width: 60, textAlign: "center" }}>
                  <Skeleton width={32} height={12} radius={4} />
                </th>
              )}
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }, (_, rowIdx) => (
            <tr key={rowIdx}>
              {hasCheckbox && (
                <td style={{ width: 40, textAlign: "center" }}>
                  <Skeleton
                    variant="rounded"
                    width={18}
                    height={18}
                    radius={4}
                  />
                </td>
              )}
              {Array.from({ length: colCount }, (_, colIdx) => {
                const isBadge = colIdx === 4 || colIdx === 5;
                return (
                  <td key={colIdx}>
                    <Skeleton
                      variant={isBadge ? "pill" : "bar"}
                      width={isBadge ? "72px" : getColWidth(colIdx)}
                      height={isBadge ? 22 : 13}
                      radius={isBadge ? 999 : 4}
                    />
                  </td>
                );
              })}
              {hasActions && (
                <td style={{ width: 60, textAlign: "center" }}>
                  <Skeleton variant="circular" width={26} height={26} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TableSkeleton.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  rows: PropTypes.number,
  hasHeader: PropTypes.bool,
  hasCheckbox: PropTypes.bool,
  hasActions: PropTypes.bool,
  variant: PropTypes.oneOf(["table", "rows", "div", "dt"]),
  className: PropTypes.string,
  style: PropTypes.object,
  columnWidths: PropTypes.array,
};
